/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: CronProfilesPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the maintenance module.
----------------------------------------------------------------*/


import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Clock } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useNotifications } from '../../contexts/NotificationContext';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { useI18n } from '../../hooks/useI18n';
import { getCronProfiles, deleteCronProfile } from './api';
import { CronProfile } from './api';

export default function CronProfilesPage() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const [data, setData] = useState<CronProfile[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; profile: CronProfile | null }>({
        isOpen: false,
        profile: null,
    });

    // State for DataGrid
    const [pagination, setPagination] = useState({ page: 1, limit: 10 });
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getCronProfiles({
                page: pagination.page,
                limit: pagination.limit,
                search: searchQuery,
                sort: sortBy,
                order: sortOrder,
            });
            setData(response.data);
            setTotal(response.pagination.total);
        } catch (error) {
            showError(error.message || t('cronProfiles.messages.fetchFailed'));
        } finally {
            setLoading(false);
        }
    }, [pagination, searchQuery, sortBy, sortOrder, showError, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleCreate = () => navigate('/admin/maintenance/cron-profiles/new');
    const handleEdit = (profile: CronProfile) => {
        const idToUse = profile.uuid ?? profile.id;
        navigate(`/admin/maintenance/cron-profiles/${idToUse}`);
    };

    const handleDelete = (profile: CronProfile) => {
        setDeleteModal({ isOpen: true, profile });
    };

    const confirmDelete = async () => {
        if (!deleteModal.profile) return;
        try {
            const idToUse = deleteModal.profile.uuid ?? deleteModal.profile.id;
            await deleteCronProfile(idToUse);
            showSuccess(t('cronProfiles.messages.deleted'));
            setDeleteModal({ isOpen: false, profile: null });
            fetchData();
        } catch (error) {
            showError(error.message || t('cronProfiles.messages.deleteFailed'));
        }
    };

    const handleSort = (column: string) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    };

    const columns = useMemo(() => [
        {
            key: 'name',
            label: t('cronProfiles.name'),
            sortable: true,
            visible: true,
            render: (_: unknown, item: CronProfile) => (
                <span className="font-medium text-primary">{item.name}</span>
            ),
        },
        {
            key: 'schedule',
            label: t('cronProfiles.cronExpression'), // Using 'cronExpression' as closest match for 'Schedule'
            sortable: true,
            visible: true,
            render: (_: unknown, item: CronProfile) => (
                <span className="font-mono text-sm text-foreground">{item.schedule}</span>
            ),
        },
        {
            key: 'enabled',
            label: t('cronProfiles.status'),
            sortable: true,
            visible: true,
            render: (_: unknown, item: CronProfile) => (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item.enabled
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                    {item.enabled ? t('common.enabled', 'Enabled') : t('common.disabled', 'Disabled')}
                </span>
            ),
        },
        {
            key: 'lastRun',
            label: t('cronProfiles.lastRun'),
            sortable: true,
            visible: true,
            render: (_: unknown, item: CronProfile) => (
                <span className="text-foreground">
                    {item.lastRun ? new Date(item.lastRun).toLocaleString() : '-'}
                </span>
            ),
        },
        {
            key: 'actions',
            label: t('common.actions', 'Actions'),
            sortable: false,
            visible: true,
            render: (_: unknown, item: CronProfile) => (
                <div className="flex items-center gap-2">
                    <SimpleTooltip content={t('common.edit', 'Edit')} position="top">
                        <button
                            onClick={() => handleEdit(item)}
                            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors"
                        >
                            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </button>
                    </SimpleTooltip>
                    <SimpleTooltip content={t('common.delete', 'Delete')} position="top">
                        <button
                            onClick={() => handleDelete(item)}
                            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors"
                        >
                            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                        </button>
                    </SimpleTooltip>
                </div>
            ),
        },
    ], [handleEdit, handleDelete, t]);

    return (
        <div className="p-6">
            <DataGridSimple
                data={data}
                loading={loading}
                columns={columns}
                pagination={{ ...pagination, total, totalPages: Math.ceil(total / pagination.limit) }}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSearch={setSearchQuery}
                onSort={handleSort}
                onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                onItemsPerPageChange={(limit) => setPagination(prev => ({ ...prev, limit, page: 1 }))}
                onColumnToggle={() => { }}
                title={t('cronProfiles.title')}
                titleIcon={<Clock className="h-5 w-5 text-primary" />}
                actions={
                    <button
                        onClick={handleCreate}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm"
                    >
                        <Plus className="h-4 w-4" />
                        <span>{t('cronProfiles.actions.add')}</span>
                    </button>
                }
            />

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, profile: null })}
                onConfirm={confirmDelete}
                title={t('cronProfiles.messages.deleteTitle')}
                message={t('cronProfiles.messages.deleteConfirm', { name: deleteModal.profile?.name })}
                confirmText={t('common.delete', 'Delete')}
                cancelText={t('common.cancel', 'Cancel')}
                type="danger"
            />
        </div>
    );
}
